import { useTranslation } from "react-i18next";
import RobinPage from "../components/RobinPage";
import { useGetAllPartnersTableQuery } from "../graphql/generated/hooks";
import { Button, Flex, Space, Table, Tooltip, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { ReloadOutlined } from "@ant-design/icons";
import { useState } from "react";
import InvitePartnerUserModal from "../components/modals/InviteUserModal";
import { EditOutlined, UserAddOutlined } from "@ant-design/icons";

function PartnerList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [inviteUserModelVisible, setInviteUserModelVisible] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number>(0);
  const [pagination, setPagination] = useState({ limit: 10, offset: 0 });

  const { data, loading, refetch } = useGetAllPartnersTableQuery({
    variables: { pagination },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const tableData = data?.getAllPartnersTable;

  const dataSource = tableData?.partners?.map((partner) => ({
    key: partner.id,
    id: partner.id,
    name: partner.name,
    nameAr: partner.nameAr,
    isActive: partner.isActive ? t("label.yes") : t("label.no"),
    storesCount: partner.storesCount || 0,
    partnerCategories: partner.partnerCategoriesCount || 0,
    plan: partner.plan?.name || "N/A",
    // add view, deactivate actions
    actions: (
      <Flex gap="small">
        <Typography.Link
          onClick={() => {
            setSelectedPartnerId(partner.id);
            setInviteUserModelVisible(true);
          }}
        >
          <Tooltip title={t("button.invite_user")}>
            <UserAddOutlined />
          </Tooltip>
        </Typography.Link>
        <Typography.Link
          onClick={() => {
            navigate(`/partners/mutate/${partner.id}`, { state: { partner } });
          }}
        >
          <Tooltip title={t("button.edit")}>
            <EditOutlined />
          </Tooltip>
        </Typography.Link>
      </Flex>
    ),
  }));

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Name (Arabic)",
      dataIndex: "nameAr",
      key: "nameAr",
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
    },
    {
      title: "Stores",
      dataIndex: "storesCount",
      key: "storesCount",
    },
    {
      title: "Categories",
      dataIndex: "partnerCategories",
      key: "partnerCategories",
    },
    {
      title: "Plan",
      dataIndex: "plan",
      key: "plan",
    },
    { title: "Actions", dataIndex: "actions", key: "actions" },
  ];
  return (
    <RobinPage
      title={t("label.menu.partner_list")}
      actions={
        <Space size="large">
          <Typography.Link
            onClick={() => {
              setPagination((p) => ({ ...p, offset: 0 }));
              refetch({ pagination: { ...pagination, offset: 0 } });
            }}
          >
            <ReloadOutlined />
          </Typography.Link>
          <Button
            onClick={() => {
              navigate("/partners/mutate");
            }}
          >
            {t("button.add")}
          </Button>
        </Space>
      }
    >
      <Table
        loading={loading}
        dataSource={dataSource}
        columns={columns}
        pagination={{
          current: Math.floor((tableData?.offset ?? pagination.offset) / (tableData?.limit ?? pagination.limit)) + 1,
          pageSize: tableData?.limit ?? pagination.limit,
          total: tableData?.total ?? 0,
          showSizeChanger: true,
          onChange: (page, pageSize) => {
            const nextLimit = pageSize ?? pagination.limit;
            const nextOffset = (page - 1) * nextLimit;
            setPagination({ limit: nextLimit, offset: nextOffset });
          },
        }}
      />
      <InvitePartnerUserModal
        isOpen={!!selectedPartnerId && inviteUserModelVisible}
        close={() => {
          setSelectedPartnerId(0);
          setInviteUserModelVisible(false);
        }}
        partnerId={selectedPartnerId}
      />
    </RobinPage>
  );
}

export default PartnerList;
